import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable, of } from "rxjs";
import { Medico } from "app/models/medico.interface";
import { GlobalVariable } from "app/global/global";
import aes from "crypto-js/aes";
import encHex from "crypto-js/enc-hex";
import padZeroPadding from "crypto-js/pad-zeropadding";
import { environment } from "environments/environment.prod";
import { Router } from "@angular/router";

@Injectable({
  providedIn: "root",
})
export class MedicoService {
  //
  url: string = GlobalVariable.BASE_API_URL + "api/medico/";
  titulo_login: string;

  //
  constructor(
    private http: HttpClient,
    private router: Router,
  ) {
    this.titulo_login = environment.MEDICO_LOGIN;
  }

  //
  listar(): Observable<any> {
    return this.http.get(this.url + "listar");
  }

  //
  formularioMedico() {
    return this.http.get(this.url + "fomulario-medico");
  }

  /**
   * Construye los campos comunes del formulario de medico
   */
  private buildMedicoFormData(
    medico: any,
    nameImage: string,
    file: File | null,
  ): FormData {
    const formData = new FormData();

    if (file) {
      formData.append("image", file);
    }

    // Campos de API Recetario
    formData.append("name", (medico.name || "").trim());
    formData.append("surname", (medico.surname || "").trim());
    formData.append("title", medico.title || "");
    formData.append("license_type", medico.licenseType || "");
    formData.append("province", medico.province || "");
    formData.append("address_street", (medico.addressStreet || "").trim());
    formData.append("address_number", (medico.addressNumber || "").trim());

    // Campos existentes
    formData.append("tipoDocumento", medico.tipoDocumento);
    formData.append("numeroDocumento", (medico.numeroDocumento || "").trim());
    formData.append("especialidad", medico.especialidad);
    formData.append("matricula", (medico.matricula || "").trim());
    formData.append("email", (medico.email || "").trim());
    formData.append("telPais", medico.telPais);
    formData.append("telCaract", (medico.telCaract || "").trim());
    formData.append("telNumero", (medico.telNumero || "").trim());
    formData.append("celPais", medico.celPais);
    formData.append("celCaract", (medico.celCaract || "").trim());
    formData.append("celNumero", (medico.celNumero || "").trim());
    formData.append("codigoInterno", (medico.codigoInterno || "").trim());
    formData.append("mp_link_pago", (medico.mpLinkPago || "").trim());
    formData.append("mp_comision_porcentaje", medico.mpComisionPorcentaje?.toString() || "");
    formData.append("nameImage", (nameImage || "ico_paciente.png").trim());
    formData.append("notificaciones", medico.notificaciones.toString());

    return formData;
  }

  private getMultipartHeaders(): HttpHeaders {
    // No setear Content-Type manualmente - Angular lo agrega automáticamente con el boundary
    return new HttpHeaders().append("Accept", "application/json");
  }

  crear(nameImage: string, medico: any, file: File): Observable<any> {
    const formData = this.buildMedicoFormData(medico, nameImage, file);

    formData.append("agenda", medico.agenda.toString());
    formData.append("color_agenda", (medico.color_agenda || "").trim());
    formData.append(
      "crearEstudiosMedicos",
      medico.crearEstudiosMedicos.toString(),
    );
    formData.append(
      "declaracion_jurada_aceptada",
      medico.declaracion_jurada_aceptada ? "true" : "false",
    );
    formData.append("activo", medico.activo ? "true" : "false");
    formData.append(
      "habilitadoRecetas",
      medico.habilitadoRecetas ? "true" : "false",
    );

    if (medico.agenda) {
      formData.append("agendaSucursales", JSON.stringify(medico.sucursales));
    }

    return this.http.post(this.url + "crear", formData, {
      headers: this.getMultipartHeaders(),
    });
  }

  borrar(form: Medico): Observable<any> {
    return this.http.delete(this.url + "borrar/" + form.id);
  }

  mostrar(id): Observable<any> {
    return this.http.get(this.url + "mostrar/" + id);
  }

  mostrarPerfil(id): Observable<any> {
    return this.http.get(this.url + "mostrar-perfil/" + id);
  }

  editar(nameImage: string, medico: any, file: any): Observable<any> {
    const formData = this.buildMedicoFormData(medico, nameImage, file);

    formData.append("id", medico.id.toString());
    formData.append("password", medico.password);
    formData.append("agenda", medico.agenda.toString());
    formData.append("color_agenda", (medico.color_agenda || "").trim());
    formData.append(
      "crearEstudiosMedicos",
      medico.crearEstudiosMedicos.toString(),
    );
    formData.append("activo", medico.activo ? "true" : "false");
    formData.append(
      "habilitadoRecetas",
      medico.habilitadoRecetas ? "true" : "false",
    );

    if (medico.agenda) {
      formData.append("agendaSucursales", JSON.stringify(medico.sucursales));
    }

    return this.http.post(this.url + "editar", formData, {
      headers: this.getMultipartHeaders(),
    });
  }

  editarPerfil(nameImage: string, medico: any, file: File): Observable<any> {
    const formData = this.buildMedicoFormData(medico, nameImage, file);

    formData.append("id", medico.id.toString());
    formData.append("password", medico.password);

    return this.http.post(this.url + "editar-perfil", formData, {
      headers: this.getMultipartHeaders(),
    });
  }

  //
  cambiarClave(id: number) {
    let formData = new FormData();
    formData.append("id", id.toString());
    return this.http.post(this.url + "cambiar-clave", formData);
  }

  //
  cantidad() {
    return this.http.get(this.url + "cantidad");
  }

  //
  login(medico: Medico) {
    const formData = new FormData();
    let key = encHex.parse("0123456789abcdef0123456789abcdef");
    let iv = encHex.parse("abcdef9876543210abcdef9876543210");
    let password = aes
      .encrypt(medico.password, key, { iv: iv, padding: padZeroPadding })
      .toString();
    formData.append("password", password);
    formData.append("email", medico.datoingreso);
    if (this.titulo_login == "MAIL") {
      formData.append("email", medico.datoingreso);
    } else {
      formData.append("numeroDocumento", medico.datoingreso);
    }
    return this.http.post(this.url + "login", formData);
  }

  //
  emailContacto(
    id: number,
    texto: string,
    filesUpload: File[],
    fileSaleSize: number,
  ) {
    const formData = new FormData();
    const headers = new HttpHeaders();

    headers.append("Content-Type", "multipart/form-data");
    headers.append("Accept", "application/json");
    if (filesUpload) {
      formData.append("cantidadArchivos", filesUpload.length.toString());
      for (let i = 0; i < filesUpload.length; i++) {
        formData.append("archivos" + i, filesUpload[i]);
      }
    }
    formData.append("id", id.toString());
    formData.append("texto", texto);
    formData.append("tipoUsuario", "medico");
    return this.http.post(this.url + "contacto", formData, {
      headers: headers,
    });
  }

  // Levanta el token del localStorage
  getToken() {
    return localStorage.getItem("auth_token");
  }

  // Chequea que el token sea valido
  isValidToken() {
    return new Promise((resolve, reject) => {
      const token = this.getToken();
      if (token) {
        const payload = this.payload(token);
        if (payload) {
          resolve(this.url + "login" === payload.iss ? true : false);
        } else {
          resolve(false);
        }
      }
    });
  }

  // Hace el parseo del jwt
  payload(token) {
    const jwtPayload = token.split(".")[1];
    return JSON.parse(atob(jwtPayload));
  }

  // Chequea que el usuario este logueado
  isLoggedIn() {
    return this.isValidToken();
  }

  //setea el token en el local storage
  handleData(response) {
    let dataToken = { token: response.token };

    localStorage.setItem("auth_token", JSON.stringify(dataToken));
    localStorage.setItem("medicoId", response.medicoId);
  }

  medicoLogeado(): Observable<any> {
    return this.http.get(this.url + "user-profile");
  }

  //
  datosAutorizacion(): Observable<any> {
    return this.http.get(this.url + "datos-autorizacion");
  }

  //
  setDatosAutorizacion(data) {
    localStorage.setItem("turno_habilitado", data.turnoHabilitado);
  }

  //
  deleteDatosAutorizacion() {
    localStorage.removeItem("turno_habilitado");
    localStorage.removeItem("auth_token");
  }

  //
  logout() {
    localStorage.removeItem("turno_habilitado");
    localStorage.removeItem("auth_token");
    localStorage.removeItem("medicoId");
    this.router.navigate(["/auth/login-medico"]);
  }

  validarRelacion(idPaciente, idMedico): Observable<any> {
    return this.http.post(this.url + "relacion-paciente", {
      idPaciente: parseInt(idPaciente),
      idMedico: parseInt(idMedico),
    });
  }

  //
  verificarTokenActivacion(token: string): Observable<any> {
    return this.http.get(this.url + "verificar-token/" + token);
  }

  //
  activarCuentaYCrearPassword(datos: any): Observable<any> {
    return this.http.post(this.url + "activar-cuenta", datos);
  }

  //
  reenviarEmailActivacion(id: number): Observable<any> {
    return this.http.post(this.url + "reenviar-email-activacion/" + id, {});
  }

  //
  getProvincias(): Observable<string[]> {
    return this.http.get<string[]>(
      GlobalVariable.BASE_API_URL + "api/recetario/provincias",
    );
  }

  // ====== RECETARIO STATUS ======

  getRecetarioStatus(): Observable<any> {
    return this.http.get(this.url + "recetario-status");
  }

  // ====== FIRMA DIGITAL ======

  /**
   * Subir firma digital del médico
   * @param medicoId ID del médico
   * @param file Archivo PNG o JPG de la firma
   */
  subirFirma(medicoId: number, file: File): Observable<FirmaResponse> {
    const formData = new FormData();
    formData.append("firma", file);

    return this.http.post<FirmaResponse>(
      `${this.url}${medicoId}/firma`,
      formData,
    );
  }

  /**
   * Obtener URL de la firma del médico
   * @param signaturePath Path almacenado en BD
   */
  getSignatureUrl(medicoId: number | null): string | null {
    if (!medicoId) return null;
    return `${this.url}${medicoId}/firma`;
  }

  descargarFirma(medicoId: number): Observable<Blob> {
    return this.http.get(`${this.url}${medicoId}/firma`, {
      responseType: "blob",
    });
  }
}

// Interface para respuesta de firma
export interface FirmaResponse {
  success: boolean;
  message: string;
  signature_path?: string;
  synced_to_api?: boolean;
  errors?: { [key: string]: string[] };
}
